// apps/dashboard/src/stores/chatStore.ts
import type { ArtifactItem, InteractivePrompt, InteractivePromptAnswer } from '@eriko/shared-types';
import { isInlineArtifactType } from '@eriko/shared-types';
import { create } from 'zustand';

export const RESUME_PLACEHOLDER_ID = 'resume-placeholder';

export interface FileReference {
  fileName: string;
  fileType: string;
  fileUrl?: string;
}

export interface ReviewerProfile {
  id: string;
  fullName: string | null;
  initials: string;
  userType: string;
}

export interface ChatMessage {
  id: string;
  // DB row id for messages created by the server during streaming. Until the
  // assistant message is persisted, `id` is a client-side placeholder
  // (`ai-${ts}`); once the server emits an event carrying the DB id
  // (e.g. interactive_prompt), we record it here so later POSTs that reference
  // the message (submitting interactive answers) can use the canonical id.
  // On conversation reload, `id` is already the DB id and this field is unset.
  dbMessageId?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  streaming?: boolean;
  files?: FileReference[];
  senderType?: 'AI' | 'REVIEWER' | 'SME';
  senderId?: string | null;
  forkNote?: { toConversationId: string; toTitle: string; reason: string };
  metadata?: { interactivePrompt?: InteractivePrompt } | null;
}

export interface PendingAnswers {
  messageId: string;
  answers: Record<string, InteractivePromptAnswer>;
}

export interface PendingSeed {
  conversationId: string;
  content: string;
  attachments: Array<{
    documentId?: string;
    fileKey: string;
    fileName: string;
    fileType: string;
    fileUrl: string;
  }>;
}

interface ChatState {
  messages: ChatMessage[];
  status: string | null;
  isStreaming: boolean;
  error: string | null;
  conversationId: string | null;
  // Conversation id for which the store's messages/artifacts are authoritative.
  // Set when either (a) a server fetch populates the store via loadConversation,
  // or (b) useChat marks the new conversation hydrated before its first [DONE].
  // ChatPage uses this to suppress the cold-load skeleton and to prevent the
  // data-push effect from overwriting local state on polling/post-stream emits.
  hydratedForConvId: string | null;
  showNewChatPlaceholder: boolean;
  artifacts: ArtifactItem[];
  activeArtifactIndex: number | null;
  reviewerProfiles: Record<string, ReviewerProfile>;
  pendingSeed: PendingSeed | null;
  pendingAnswers: PendingAnswers | null;
  // Resume mode: true when the last persisted message is user-authored
  // (SME/REVIEWER) on chat page mount, meaning LangGraph is still running
  // on the server after an SSE disconnect. Drives the polling + placeholder.
  resumeActive: boolean;

  addUserMessage: (content: string, files?: FileReference[]) => void;
  startAssistantMessage: () => string;
  appendToken: (id: string, content: string) => void;
  finalizeAssistantMessage: (id: string) => void;
  setStatus: (status: string | null) => void;
  setIsStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  setConversationId: (conversationId: string | null) => void;
  addArtifact: (artifact: ArtifactItem) => void;
  setActiveArtifact: (index: number | null) => void;
  loadConversation: (conversationId: string, messages: ChatMessage[], artifacts?: ArtifactItem[]) => void;
  prepareForConversation: (conversationId: string) => void;
  markHydrated: (conversationId: string) => void;
  reset: () => void;
  setReviewerProfile: (profile: ReviewerProfile) => void;
  setPendingSeed: (seed: PendingSeed | null) => void;
  setPendingAnswer: (rowId: string, answer: InteractivePromptAnswer, messageId: string) => void;
  clearPendingAnswers: () => void;
  setResumeActive: (active: boolean) => void;
  injectResumePlaceholder: () => void;
  clearResumePlaceholder: () => void;
  setMessageMetadata: (messageId: string, metadata: ChatMessage['metadata']) => void;
  setMessageDbId: (clientId: string, dbMessageId: string) => void;
}

function getTimestamp() {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  status: null,
  isStreaming: false,
  error: null,
  conversationId: null,
  hydratedForConvId: null,
  showNewChatPlaceholder: false,
  artifacts: [],
  activeArtifactIndex: null,
  reviewerProfiles: {},
  pendingSeed: null,
  pendingAnswers: null,
  resumeActive: false,

  addUserMessage: (content, files) =>
    set((s) => ({
      messages: [
        ...s.messages,
        { id: `user-${Date.now()}`, role: 'user', content, timestamp: getTimestamp(), files },
      ],
    })),

  startAssistantMessage: () => {
    const id = `ai-${Date.now()}`;
    set((s) => ({
      messages: [
        ...s.messages,
        { id, role: 'assistant', content: '', timestamp: getTimestamp(), streaming: true },
      ],
    }));
    return id;
  },

  appendToken: (id, content) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, content: m.content + content } : m)),
    })),

  finalizeAssistantMessage: (id) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
    })),

  setStatus: (status) => set({ status }),
  setIsStreaming: (isStreaming) => set({ isStreaming }),
  setError: (error) => set({ error }),
  setConversationId: (conversationId) => set({ conversationId, showNewChatPlaceholder: false }),

  addArtifact: (artifact) =>
    set((s) => {
      const newArtifacts = [...s.artifacts, artifact];
      // Inline-only chat-card artifacts render inside the assistant bubble,
      // not in the side panel. Adding one should NOT auto-open the panel
      // (and falling through to GenericArtifactView would just show raw JSON).
      return {
        artifacts: newArtifacts,
        activeArtifactIndex: isInlineArtifactType(artifact.type)
          ? s.activeArtifactIndex
          : newArtifacts.length - 1,
      };
    }),

  setActiveArtifact: (index) => set({ activeArtifactIndex: index }),

  loadConversation: (conversationId, messages, artifacts) => {
    // Auto-open the latest *side-panel* artifact when loading a conversation —
    // skip inline-only chat-card artifacts (reconciliation-list, match-confirmed)
    // since those render inside the assistant bubble, not in the panel.
    let lastPanelIndex: number | null = null;
    if (artifacts && artifacts.length > 0) {
      for (let i = artifacts.length - 1; i >= 0; i--) {
        if (!isInlineArtifactType(artifacts[i]?.type)) {
          lastPanelIndex = i;
          break;
        }
      }
    }
    set({
      conversationId,
      hydratedForConvId: conversationId,
      messages: [...messages],
      status: null,
      isStreaming: false,
      error: null,
      showNewChatPlaceholder: false,
      artifacts: artifacts ?? [],
      activeArtifactIndex: lastPanelIndex,
      pendingAnswers: null,
      resumeActive: false,
    });
  },

  prepareForConversation: (conversationId) =>
    set({
      conversationId,
      hydratedForConvId: null,
      messages: [],
      status: null,
      isStreaming: false,
      error: null,
      showNewChatPlaceholder: false,
      artifacts: [],
      activeArtifactIndex: null,
      resumeActive: false,
    }),

  markHydrated: (conversationId) => set({ hydratedForConvId: conversationId }),

  reset: () =>
    set({
      messages: [],
      status: null,
      isStreaming: false,
      error: null,
      conversationId: null,
      hydratedForConvId: null,
      showNewChatPlaceholder: true,
      artifacts: [],
      activeArtifactIndex: null,
      pendingAnswers: null,
      resumeActive: false,
    }),

  setReviewerProfile: (profile) =>
    set((s) => ({ reviewerProfiles: { ...s.reviewerProfiles, [profile.id]: profile } })),

  setPendingSeed: (seed) => set({ pendingSeed: seed }),

  setPendingAnswer: (rowId, answer, messageId) =>
    set((s) => {
      const current =
        s.pendingAnswers?.messageId === messageId
          ? s.pendingAnswers
          : { messageId, answers: {} };
      return {
        pendingAnswers: {
          messageId,
          answers: { ...current.answers, [rowId]: answer },
        },
      };
    }),

  clearPendingAnswers: () => set({ pendingAnswers: null }),

  setResumeActive: (active) => set({ resumeActive: active }),

  injectResumePlaceholder: () =>
    set((s) => {
      if (s.messages.some((m) => m.id === RESUME_PLACEHOLDER_ID)) return {};
      return {
        messages: [
          ...s.messages,
          {
            id: RESUME_PLACEHOLDER_ID,
            role: 'assistant',
            content: '',
            timestamp: getTimestamp(),
            streaming: true,
            senderType: 'AI',
          },
        ],
      };
    }),

  clearResumePlaceholder: () =>
    set((s) => ({
      messages: s.messages.filter((m) => m.id !== RESUME_PLACEHOLDER_ID),
    })),

  setMessageMetadata: (messageId, metadata) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, metadata } : m)),
    })),

  setMessageDbId: (clientId, dbMessageId) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === clientId ? { ...m, dbMessageId } : m)),
    })),
}));
